import React, { createContext, useContext, useMemo } from "react";

import { DEFAULT_PAGE_PERMISSIONS } from "@/constants/pagePermissionDefaults";
import { usePagePermissionsQuery } from "@/hook/usePagePermissions";
import type { PagePermissionsContextType } from "@/interface/PagePermissions";

export const PagePermissionsContext = createContext<PagePermissionsContextType | undefined>(
  undefined
);

export const usePagePermissions = (): PagePermissionsContextType => {
  const context = useContext(PagePermissionsContext);
  if (!context) {
    throw new Error("usePagePermissions must be used within PagePermissionsProvider");
  }
  return context;
};

export const PagePermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, isLoading, refetch } = usePagePermissionsQuery();

  const permissions = useMemo(() => {
    // Start with defaults
    const merged: Record<string, string[]> = { ...DEFAULT_PAGE_PERMISSIONS };

    // Override with API values if available
    if (data?.permissions) {
      for (const entry of data.permissions) {
        merged[entry.pageId] = [...entry.allowedRoles];
      }
    }

    // Safety net: ensure Super Admin is always present in every entry
    for (const pageId of Object.keys(merged)) {
      if (!merged[pageId].includes("Super Admin")) {
        merged[pageId] = ["Super Admin", ...merged[pageId]];
      }
    }

    return merged;
  }, [data]);

  const hasPageAccess = (pageId: string, role: string): boolean => {
    const allowed = permissions[pageId];
    // Unknown page = deny. An unregistered pageId is a wiring mistake, not an
    // open page; failing closed keeps it out of the nav and off the router.
    if (!allowed) return false;
    return allowed.includes(role);
  };

  const getAllowedRoles = (pageId: string): string[] => {
    return (
      permissions[pageId] ??
      DEFAULT_PAGE_PERMISSIONS[pageId as keyof typeof DEFAULT_PAGE_PERMISSIONS] ??
      []
    );
  };

  return (
    <PagePermissionsContext.Provider
      value={{ permissions, isLoading, hasPageAccess, getAllowedRoles, refetch }}
    >
      {children}
    </PagePermissionsContext.Provider>
  );
};
